import { SessionStore } from "../uidispatcherapi/SessionStore";
import { url } from "../utils/UrlUtil";
import { Request } from "../stores/Requests20";
import { CreditCheckInfo } from "../stores/CheckoutTypes";
import { parseCheckoutData } from "../model/CheckoutData";
import { ServiceError, Callback, Country, StoreId, UserId, BettyId, Money } from "../stores/BaseTypes";
import type { CheckoutData, PaymentType } from "../model/CheckoutData";
import type { CountryOpeningHours, CreditLimitPolicyResponse } from "../model/StoreInformation";
import type {
	CheckoutUserData,
	CreateCheckoutRequest,
	LoadCheckoutRequest,
	PutPaymentMethodSelectionRequest,
	SelectAddressRequest,
	SetDeliveryPhoneNumberRequest,
	SubmitCheckoutRequest,
	UpdateHeaderRequest,
	UpdateServiceTypeSelectionRequest,
	RemoveItemsRequest,
	VoucherRequest,
	SelectPickupStoreRequest,
	SetEmailAddressRequest,
} from "../model/CheckoutRequests";
import type { PaymentPreauthorization, PaymentPreauthorizationRequestData } from "../stores/CheckoutTypes";
type ErrorCallback = Callback<ServiceError>;

function checkoutUrlParams(data: CheckoutUserData, ...extraParams: string[]): string {
	return [data.country, data.storeId, data.customerId, data.additionalId, ...extraParams]
		.map(encodeURIComponent)
		.join("/");
}

export function getBackendLocale() {
	return SessionStore.getLocale().replace("-", "_");
}
const CHECKOUT_BASE_URL = "/ordercapture/checkout";
const CHECKOUT_TIMEOUT_LONG_MS = 30000;
export const CHECKOUT_TIMEOUT_MS = 5000;
export type HipayBrowserInfo = {
	isJavaEnabled?: boolean;
	isJavascriptEnabled?: boolean;
	language?: string | null;
	colorDepth?: number | null;
	screenHeight?: number | null;
	screenWidth?: number | null;
	timezoneOffsetInMinutes?: number | null;
};
export function getHipayBrowserInfo(): {
	deviceChannel: string;
	hipayBrowserInfo: HipayBrowserInfo;
} {
	return {
		deviceChannel: "BROWSER",
		hipayBrowserInfo: {
			isJavaEnabled: false,
			isJavascriptEnabled: true,
			language: window && window.navigator && window.navigator.language,
			colorDepth: window && window.screen && window.screen.colorDepth,
			screenHeight: window && window.screen && window.screen.height,
			screenWidth: window && window.screen && window.screen.width,
			timezoneOffsetInMinutes: new Date().getTimezoneOffset(),
		},
	};
}
export async function handleServiceResponse(responsePromise: Promise<Response>): Promise<Record<string, any>> {
	const response = await responsePromise;
	let json: Record<string, any>;
	let text: string;

	try {
		text = await response.text();
		json = response.status !== 204 ? JSON.parse(text) : {};
	} catch (err: any) {
		if (!response.ok) {
			throw new ServiceError({
				code: "OC-998",
				value: "error",
				message: String(response.status),
				type: "Server",
			});
		}

		throw new ServiceError({
			message: err.message,
			value: "error",
			code: "OC-998",
			type: "Parsing",
		});
	}

	if (!response.ok) {
		const errors = json && json.errors;
		throw new ServiceError({
			code: errors?.[0]?.code || "OC-998",
			value: errors?.[0]?.value || null,
			message: errors?.[0]?.message || errors,
			type: "Server",
		});
	}

	return json;
}
// ==========
// Checkout
// ==========
export function submitCheckout(
	params: SubmitCheckoutRequest,
	callback: Callback<CheckoutData>,
	errorCallback: ErrorCallback,
): void {
	const requestUrl =
		CHECKOUT_BASE_URL +
		"/checkout/submit/" +
		checkoutUrlParams(params.userData, params.checkoutId) +
		"?locale=" +
		(params.locale || "");
	const responsePromise = Request.post(requestUrl)
		.timeout(CHECKOUT_TIMEOUT_LONG_MS)
		.execute({ ...params.submitEvent, useNewPaymentSelection: true, ...getHipayBrowserInfo() });
	handleServiceResponse(responsePromise)
		.then((json) => json.data)
		.then((data) => parseCheckoutData(data))
		.then(callback)
		.catch(errorCallback);
}
export function createAndSubmitCheckout(
	params: CreateCheckoutRequest,
	callback: Callback<CheckoutData>,
	errorCallback: ErrorCallback,
): void {
	if (!params.cartId || !params.cartVersion) {
		errorCallback(
			new ServiceError({
				code: "OC-999",
				message: "No cart data was provided",
			}),
		);
		return;
	}

	const requestUrl =
		CHECKOUT_BASE_URL +
		"/checkout/instant-submissions/" +
		checkoutUrlParams(params.userData, params.cartId, params.cartVersion.toString());
	const responsePromise = Request.post(requestUrl)
		.retry(true)
		.timeout(CHECKOUT_TIMEOUT_LONG_MS)
		.execute(params.creationContext);
	handleServiceResponse(responsePromise)
		.then((json) => parseCheckoutData(json.data))
		.then(callback)
		.catch(errorCallback);
}
export function loadCheckout(
	userData: LoadCheckoutRequest,
	callback: Callback<CheckoutData>,
	errorCallback: ErrorCallback,
): void {
	const requestUrl = CHECKOUT_BASE_URL + "/checkout/" + checkoutUrlParams(userData);
	const responsePromise = Request.get(requestUrl).timeout(CHECKOUT_TIMEOUT_MS).execute();
	handleServiceResponse(responsePromise)
		.then((json) => parseCheckoutData(json.data))
		.then(callback)
		.catch(errorCallback);
}
export function loadCheckoutFromId(
	checkoutId: string,
	callback: Callback<CheckoutData>,
	errorCallback: ErrorCallback,
): void {
	const requestUrl = CHECKOUT_BASE_URL + "/checkout/" + checkoutId + "/technical";
	const responsePromise = Request.get(requestUrl).timeout(CHECKOUT_TIMEOUT_MS).execute();
	handleServiceResponse(responsePromise)
		.then((json) => parseCheckoutData(json.data))
		.then(callback)
		.catch(errorCallback);
}
export function loadCheckoutWithAlternativeServiceTypes(
	userData: LoadCheckoutRequest,
	callback: Callback<CheckoutData>,
	errorCallback: ErrorCallback,
): void {
	const requestUrl = CHECKOUT_BASE_URL + "/checkout/all/" + checkoutUrlParams(userData);
	const responsePromise = Request.get(requestUrl).timeout(CHECKOUT_TIMEOUT_MS).execute();
	handleServiceResponse(responsePromise)
		.then((json) => parseCheckoutData(json.data.checkoutData))
		.then(callback)
		.catch(errorCallback);
}
export function createCheckout(
	params: CreateCheckoutRequest,
	callback: Callback<CheckoutData>,
	errorCallback: ErrorCallback,
): void {
	if (!params.cartId || !params.cartVersion) {
		errorCallback(
			new ServiceError({
				code: "OC-999",
				message: "No cart data was provided",
			}),
		);
		return;
	}

	const requestUrl =
		CHECKOUT_BASE_URL +
		"/checkout/create/" +
		checkoutUrlParams(params.userData, params.cartId, params.cartVersion.toString());
	const responsePromise = Request.post(requestUrl)
		.retry(true)
		.timeout(CHECKOUT_TIMEOUT_MS)
		.execute(params.creationContext);
	handleServiceResponse(responsePromise)
		.then((json) => parseCheckoutData(json.data.checkoutData))
		.then(callback)
		.catch(errorCallback);
}
export function updateCheckoutHeader(
	params: UpdateHeaderRequest,
	callback: Callback<CheckoutData>,
	errorCallback: ErrorCallback,
): void {
	const requestUrl =
		CHECKOUT_BASE_URL + "/checkout/header-update/" + checkoutUrlParams(params.userData, params.checkoutId);
	const responsePromise = Request.put(requestUrl).timeout(CHECKOUT_TIMEOUT_MS).execute(params.event);
	handleServiceResponse(responsePromise)
		.then((json) => parseCheckoutData(json.data))
		.then(callback)
		.catch(errorCallback);
}
export function putPaymentMethodSelection(
	params: PutPaymentMethodSelectionRequest,
	callback: Callback<CheckoutData>,
	errorCallback: Callback<ServiceError>,
): void {
	const requestUrl =
		CHECKOUT_BASE_URL + "/checkout/payment-method/" + checkoutUrlParams(params.userData, params.checkoutId);
	const responsePromise = Request.put(requestUrl).timeout(CHECKOUT_TIMEOUT_MS).execute(params.event);
	handleServiceResponse(responsePromise)
		.then((json) => parseCheckoutData(json.data))
		.then(callback)
		.catch(errorCallback);
}
export function updateServiceTypeSelection(
	params: UpdateServiceTypeSelectionRequest,
	callback: Callback<CheckoutData>,
	errorCallback: ErrorCallback,
): void {
	const requestUrl =
		CHECKOUT_BASE_URL + "/checkout/delivery-mode/" + checkoutUrlParams(params.userData, params.checkoutId);
	const responsePromise = Request.put(requestUrl).timeout(CHECKOUT_TIMEOUT_MS).execute(params.serviceTypeSelection);
	handleServiceResponse(responsePromise)
		.then((json) => parseCheckoutData(json.data.checkoutData))
		.then(callback)
		.catch(errorCallback);
}
export function selectAddress(
	params: SelectAddressRequest,
	callback: Callback<CheckoutData>,
	errorCallback: ErrorCallback,
): void {
	const requestUrl = CHECKOUT_BASE_URL + "/checkout/address/" + checkoutUrlParams(params.userData, params.checkoutId);
	const responsePromise = Request.post(requestUrl).timeout(CHECKOUT_TIMEOUT_MS).execute(params.addressSelection);
	handleServiceResponse(responsePromise)
		.then((json) => parseCheckoutData(json.data))
		.then(callback)
		.catch(errorCallback);
}
export function setDeliveryPhoneNumber(
	params: SetDeliveryPhoneNumberRequest,
	callback: Callback<CheckoutData>,
	errorCallback: ErrorCallback,
): void {
	const requestUrl =
		CHECKOUT_BASE_URL + "/checkout/delivery-phone-number/" + checkoutUrlParams(params.userData, params.checkoutId);
	const responsePromise = Request.put(requestUrl)
		.timeout(CHECKOUT_TIMEOUT_MS)
		.execute(params.deliveryPhoneNumberSelection);
	handleServiceResponse(responsePromise)
		.then((json) => parseCheckoutData(json.data))
		.then(callback)
		.catch(errorCallback);
}
export function setEmailAddress(
	params: SetEmailAddressRequest,
	callback: Callback<CheckoutData>,
	errorCallback: ErrorCallback,
): void {
	const requestUrl =
		CHECKOUT_BASE_URL + "/checkout/email-address/" + checkoutUrlParams(params.userData, params.checkoutId);
	const responsePromise = Request.put(requestUrl).timeout(CHECKOUT_TIMEOUT_MS).execute(params.emailAddressSelection);
	handleServiceResponse(responsePromise)
		.then((json) => parseCheckoutData(json.data))
		.then(callback)
		.catch(errorCallback);
}
export function selectPickupStore(
	params: SelectPickupStoreRequest,
	callback: Callback<CheckoutData>,
	errorCallback: ErrorCallback,
): void {
	const requestUrl = CHECKOUT_BASE_URL + "/checkout/pickup/" + checkoutUrlParams(params.userData, params.checkoutId);
	const responsePromise = Request.post(requestUrl).timeout(CHECKOUT_TIMEOUT_MS).execute(params.pickupStoreSelection);
	handleServiceResponse(responsePromise)
		.then((json) => parseCheckoutData(json.data))
		.then(callback)
		.catch(errorCallback);
}
export function removeItems(
	params: RemoveItemsRequest,
	callback: Callback<CheckoutData>,
	errorCallback: ErrorCallback,
): void {
	const requestUrl =
		CHECKOUT_BASE_URL + "/checkout/excluded-items/" + checkoutUrlParams(params.userData, params.checkoutId);
	const responsePromise = Request.post(requestUrl).timeout(CHECKOUT_TIMEOUT_MS).execute(params.items);
	handleServiceResponse(responsePromise)
		.then((json) => parseCheckoutData(json.data))
		.then(callback)
		.catch(errorCallback);
}
export function putVoucher(
	params: VoucherRequest,
	callback: Callback<CheckoutData>,
	errorCallback: ErrorCallback,
): void {
	const requestUrl =
		CHECKOUT_BASE_URL + "/checkout/" + checkoutUrlParams(params.userData, params.checkoutId) + "/voucher";
	const responsePromise = Request.put(requestUrl).timeout(CHECKOUT_TIMEOUT_LONG_MS).execute({
		value: params.code,
	});
	handleServiceResponse(responsePromise)
		.then((json) => parseCheckoutData(json.data))
		.then(callback)
		.catch(errorCallback);
}
export function deleteVoucher(
	params: VoucherRequest,
	callback: Callback<CheckoutData>,
	errorCallback: ErrorCallback,
): void {
	const requestUrl =
		CHECKOUT_BASE_URL +
		"/checkout/" +
		checkoutUrlParams(params.userData, params.checkoutId, params.code) +
		"/voucher";
	const responsePromise = Request.delete(requestUrl).timeout(CHECKOUT_TIMEOUT_MS).execute();
	handleServiceResponse(responsePromise)
		.then((json) => parseCheckoutData(json.data))
		.then(callback)
		.catch(errorCallback);
}
export function getSubmittedCheckoutTechnical(checkoutId: string): Promise<CheckoutData> {
	const requestUrl = `${CHECKOUT_BASE_URL}/checkout/${checkoutId}/technical`;
	const responsePromise = Request.get(requestUrl).timeout(CHECKOUT_TIMEOUT_LONG_MS).execute();
	return handleServiceResponse(responsePromise).then((json) => json.data);
}

// ==========
// PSP
// ==========
function parsePaymentPreauthorization(json: Record<string, any>): PaymentPreauthorization {
	return {
		forwardUrl: json.data.forwardUrl,
	};
}

export function preauthorizePayment(
	requestData: PaymentPreauthorizationRequestData,
	paymentType: PaymentType,
	successCallback: Callback<PaymentPreauthorization>,
	errorCallback: Callback<ServiceError>,
) {
	const country = requestData.country;
	const storeId = requestData.storeId;
	const customerId = requestData.customerId;
	const additionalId = requestData.additionalId;
	const checkoutId = requestData.checkoutId;
	const requestBody = {
		paymentType,
		amount: requestData.amount,
		baseErrorUrl: requestData.baseErrorUrl,
		baseReturnUrl: requestData.baseReturnUrl,
		currency: requestData.currency,
		description: requestData.description,
		language: requestData.language,
		multiUse: requestData.multiUse,
		...getHipayBrowserInfo(),
	};
	const requestUrl = `${CHECKOUT_BASE_URL}/preauth/${country}/${storeId}/${customerId}/${additionalId}/${checkoutId}`;
	const responsePromise = Request.post(requestUrl).timeout(CHECKOUT_TIMEOUT_LONG_MS).execute(requestBody);
	handleServiceResponse(responsePromise)
		.then(parsePaymentPreauthorization)
		.then(successCallback)
		.catch(errorCallback);
}
// ==========
// StoreInformation
// ==========
export function fetchCreditLimitPolicy(params: { country: string }): Promise<CreditLimitPolicyResponse> {
	const requestUrl = url`/ordercapture/storeinfo/credit-limit/${params.country}`;
	const response = Request.get(requestUrl).timeout(CHECKOUT_TIMEOUT_LONG_MS).execute();
	return handleServiceResponse(response).then((data) => data.data);
}
// ==========
// StoreInformation
// ==========
export function fetchStoreOpeningHours(params: { country: string; locale: string }): Promise<CountryOpeningHours> {
	const countryCode = params.country.toUpperCase();
	const requestUrl = url`/explore.content.v1/sitecore/storeinformation/${countryCode}/${params.locale}`;
	const response = Request.get(requestUrl).timeout(CHECKOUT_TIMEOUT_LONG_MS).execute();
	return handleServiceResponse(response) as Promise<CountryOpeningHours>;
}
// ==========
// Credit check
// ==========
export function fetchCreditCheck(params: {
	customerId: string;
	totalGrossAmount: number;
	storeId: string;
	country: string;
}): Promise<CreditCheckInfo> {
	const customerId = params.customerId;
	const countryCode = params.country.toUpperCase();
	const requestUrl = url`/orderfulfillment.orderservice.v1/creditcheck/${customerId}?total=0&country=${countryCode}`;
	const responsePromise = Request.get(requestUrl).execute();
	return handleServiceResponse(responsePromise) as Promise<CreditCheckInfo>;
}
// ==========
// Employee price
// ==========
export type ApprovalData = {
	minPrice: Money;
	approvalLimit: Money;
	mayMarkBbd: boolean;
};
export function fetchLowestPriceForBettyId(
	country: Country,
	storeId: StoreId,
	userId: UserId,
	addressId: string | null,
	bettyId: BettyId,
	employeeId: string,
): Promise<ApprovalData> {
	const requestUrl =
		url`/ordercapture/employeeprice/approvals/employeeApprovalData/${country}/${storeId}/${employeeId}/${userId}/article/${bettyId}` +
		(addressId ? url`?addressId=${addressId}` : "");
	return handleServiceResponse(Request.get(requestUrl).execute()) as Promise<ApprovalData>;
}
// ==========
// Available Payment Types
// ==========
export type PaymentId = {
	paymentAllowance: string;
	paymentType: string | null | undefined;
};
